import { AnsweringClient } from "../clients/answeringClient";
import { getCodeSession } from "../utils/session";

/**
 * What the answering screens need in react-router state to mount. Instructions
 * reads `state.data.instrumentId` in its constructor
 * (`InstructionsScreen/Instructions.tsx:55`), so arriving without it crashes the
 * screen — which is why the deep link cannot be a bare `window.location.href`.
 */
export interface ICodeDestination {
  assignmentId: number;
  instrumentId: number | null;
  applicationId: number | null;
  /** Already answered: belongs on the finish screen, not on instructions. */
  isFinished: boolean;
  /** Past its due date: the list refuses to open it, and so do we. */
  isLate: boolean;
  instrumentTitle: string;
}

export interface CodeControllerDelegate {
  /** The code was accepted; carries what the destination screen must receive. */
  didAuthenticate(destination: ICodeDestination): void;
  /** The code was wrong, or the input was empty — the person may try again. */
  codeRejected(message: string): void;
  /** Locked out. Retrying cannot help; only the psychologist can. */
  lockedOut(message: string): void;
  /** Terminal: the answering window closed, or the coleta is gone. */
  windowClosed(message: string): void;
  /** Terminal: this person now has an account and must use e-mail + password. */
  shouldUsePassword(message: string): void;
  /** The service could not answer. Retrying later may work. */
  serviceUnavailable(message: string): void;
}

/**
 * Canonical form of the code, mirroring the backend's normalizeAccessCode so
 * the field accepts what a person actually types — lowercase, spaces, or the
 * lookalikes Crockford base32 excludes (I/L→1, O→0).
 */
export function normalizeCode(raw: string): string {
  return (raw ?? "")
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "")
    .replace(/O/g, "0")
    .replace(/[IL]/g, "1");
}

export class CodeController {
  constructor(
    private delegate: CodeControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {}

  /**
   * Exchanges the invitation uid + code for a session. Stores ONLY the access
   * token: the backend issues no refresh token for a code session, and writing
   * a `RefreshToken` key here — even an empty one — would put this session back
   * on the refresh-then-logout path that ends at the auth-fe login, where the
   * person has no password to type.
   */
  public async login(invitationUid: string, rawCode: string) {
    const code = normalizeCode(rawCode);
    if (!code) {
      this.delegate.codeRejected("Digite o código que você recebeu.");
      return;
    }

    try {
      const response = await this.answeringClient.authCode(invitationUid, code);
      const accessToken = response.data?.accessToken;
      if (!accessToken) {
        this.delegate.serviceUnavailable(
          "Não foi possível entrar agora. Tente novamente."
        );
        return;
      }

      localStorage.setItem("AccessToken", accessToken);
      localStorage.removeItem("RefreshToken");

      // The coleta to open comes from the token's own claim rather than from a
      // response field: it is the same value the backend will enforce on every
      // later request, so reading it here cannot drift from what is allowed.
      const session = getCodeSession(accessToken);
      if (!session) {
        this.delegate.serviceUnavailable(
          "Não foi possível identificar a sua coleta. Tente novamente."
        );
        return;
      }

      const destination = await this.resolveDestination(
        accessToken,
        session.codeAssignment
      );
      if (!destination) {
        this.delegate.serviceUnavailable(
          "Não foi possível carregar a sua coleta. Tente novamente."
        );
        return;
      }

      this.delegate.didAuthenticate(destination);
    } catch (error: any) {
      this.reportError(error);
    }
  }

  /**
   * Reads the coleta's own row from the assigned listing, which a code session
   * receives already filtered to the single assignment its claim allows. The
   * detour is not optional: the answering screens take `instrumentId` and
   * `applicationId` through router state, and the verify response carries
   * neither — so without this the deep link mounts a screen with null state.
   */
  private async resolveDestination(
    accessToken: string,
    assignmentId: number
  ): Promise<ICodeDestination | null> {
    const response = await this.answeringClient.getAssignedInstruments(
      accessToken
    );
    const assignments: any[] = response.data || [];
    const assignment = assignments.find(
      (candidate) => Number(candidate.assignmentId) === assignmentId
    );
    if (!assignment) return null;

    const dueDate = assignment.dueDate ? new Date(assignment.dueDate) : null;
    const isFinished = Boolean(assignment.finishedAt);

    return {
      assignmentId,
      instrumentId: assignment.instrumentId ?? null,
      applicationId: assignment.applicationId ?? null,
      isFinished,
      // Same rule the assignments list uses to decide a card is unopenable.
      isLate: !isFinished && dueDate !== null && dueDate < new Date(),
      instrumentTitle: assignment.title ?? "",
    };
  }

  /**
   * Maps the verify endpoint's contract onto delegate calls. The distinctions
   * are the point: only a wrong code should invite another attempt, and 410
   * must never send the person back to the field — re-entering a correct code
   * cannot reopen a closed window.
   */
  private reportError(error: any): void {
    const status = error?.response?.status;
    const message = error?.response?.data?.message;

    switch (status) {
      case 401:
        this.delegate.codeRejected(
          message || "Código inválido. Confira e tente novamente."
        );
        return;
      case 429:
        this.delegate.lockedOut(
          message ||
            "Muitas tentativas. Peça um novo código a quem encaminhou o instrumento para você responder."
        );
        return;
      case 410:
        this.delegate.windowClosed(
          message || "O prazo para responder este instrumento encerrou."
        );
        return;
      case 409:
        this.delegate.shouldUsePassword(
          message ||
            "Este convite já foi convertido em conta. Entre com e-mail e senha."
        );
        return;
      default:
        this.delegate.serviceUnavailable(
          message || "Não foi possível entrar agora. Tente novamente."
        );
    }
  }
}
